import { useState, useEffect, useCallback, useRef } from 'react';
import { readMainBundle, clearAppCachesAndReload } from '../utils/forceUpdate';

// Point 3 — Auto-update detection for admin/handler sessions.
// Uses the SAME source the app already trusts: /asset-manifest.json, which CRA
// regenerates with a new content-hashed main.<hash>.js on every production
// build. We snapshot the bundle currently deployed at boot (baseline), then
// poll every ~18 min and compare. A mismatch = a newer version is live.
// In dev / when the manifest is unavailable it returns null → never flags,
// so there are no false popups.

const POLL_MS = 18 * 60 * 1000; // 18 minutes

// Short, human-friendly label for the Settings card (e.g. "a1b2c3d4").
export const shortVersion = (bundle) => {
  if (!bundle) return 'dev';
  const m = String(bundle).match(/\.([A-Za-z0-9]+)\.js$/) || String(bundle).match(/main\.([A-Za-z0-9]+)/);
  return m ? m[1].slice(0, 8) : String(bundle);
};

export function useAppUpdate(enabled) {
  const baselineRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [latest, setLatest] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [checking, setChecking] = useState(false);

  const check = useCallback(async () => {
    if (!enabled) return false;
    setChecking(true);
    try {
      const l = await readMainBundle();
      if (l && !baselineRef.current) {
        baselineRef.current = l; // first successful read = the running version
        setCurrent(l);
      }
      if (l) setLatest(l);
      const base = baselineRef.current;
      const avail = !!(l && base && l !== base);
      setUpdateAvailable(avail);
      return avail;
    } catch {
      return false;
    } finally {
      setChecking(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    check();
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, check]);

  // Reuse the app's proven cache-bust: unregisters SW + clears Cache Storage +
  // cache-busts index.html. localStorage (dws_user / session) is untouched →
  // the login session survives the reload.
  const applyUpdate = useCallback(async () => {
    await clearAppCachesAndReload();
  }, []);

  return { current, latest, updateAvailable, checking, check, applyUpdate, POLL_MS };
}

export default useAppUpdate;
